import React, { useEffect, useState } from 'react';
import { Turf, Match } from '../types';
import {
  MapPin, Phone, Star, Navigation, Plus, Pencil, Trash2, Link2, Search, X, Loader2,
  Landmark, Clock, Users, MessageCircle, Building2,
} from 'lucide-react';

interface TurfDirectoryViewProps {
  isOrganizer: boolean;
  userId?: string;
  matches: Match[];
  onAttachTurf: (matchId: string, turf: Turf) => void;
}

type TurfForm = {
  name: string;
  area: string;
  address: string;
  pricePerHour: number;
  size: string;
  facilities: string;
  isIndoor: boolean;
  phone: string;
  whatsapp: string;
  imageUrl: string;
  mapsUrl: string;
  maxCapacity: string;
  notes: string;
};

const emptyForm = (): TurfForm => ({
  name: '', area: '', address: '', pricePerHour: 2000, size: '7v7',
  facilities: '', isIndoor: false, phone: '', whatsapp: '',
  imageUrl: '', mapsUrl: '', maxCapacity: '', notes: '',
});

const whatslink = (n?: string) => `https://wa.me/${(n || '').replace(/\D/g, '')}`;
const tellink = (n?: string) => `tel:${(n || '').replace(/\D/g, '')}`;

export const TurfDirectoryView: React.FC<TurfDirectoryViewProps> = ({ isOrganizer, userId, matches, onAttachTurf }) => {
  const [turfs, setTurfs] = useState<Turf[]>([]);
  const [areas, setAreas] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [areaFilter, setAreaFilter] = useState('All');
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState('');

  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState<TurfForm>(emptyForm());

  const [linkTurf, setLinkTurf] = useState<Turf | null>(null);
  const [linkMatchId, setLinkMatchId] = useState('');

  const load = async () => {
    try {
      const res = await fetch('/api/turfs');
      const data = await res.json();
      setTurfs(data.turfs || []);
      setAreas(data.areas || []);
    } catch {
      setNotice('Could not load the turf directory right now.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const canManage = (t: Turf) => isOrganizer || (userId != null && t.createdBy === userId);

  const openAdd = () => { setEditId(null); setForm(emptyForm()); setShowModal(true); setNotice(''); };
  const openEdit = (t: Turf) => {
    setEditId(t.id);
    setForm({
      name: t.name, area: t.area, address: t.address, pricePerHour: t.pricePerHour, size: t.size,
      facilities: (t.facilities || []).join(', '), isIndoor: t.isIndoor, phone: t.phone || '',
      whatsapp: t.whatsapp || '', imageUrl: t.imageUrl || '', mapsUrl: t.mapsUrl || '',
      maxCapacity: t.maxCapacity != null ? String(t.maxCapacity) : '', notes: t.notes || '',
    });
    setShowModal(true);
    setNotice('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) { setNotice('Turf name is required.'); return; }
    setBusy(true); setNotice('');
    const payload = {
      name: form.name.trim(), area: form.area.trim(), address: form.address.trim(),
      pricePerHour: Math.max(0, Number(form.pricePerHour) || 0), size: form.size.trim() || '7v7',
      facilities: form.facilities.split(',').map((s) => s.trim()).filter(Boolean),
      isIndoor: form.isIndoor, phone: form.phone.trim(), whatsapp: form.whatsapp.trim(),
      imageUrl: form.imageUrl.trim(), mapsUrl: form.mapsUrl.trim(),
      maxCapacity: form.maxCapacity.trim() ? Number(form.maxCapacity.trim()) : undefined,
      notes: form.notes.trim(),
    };
    try {
      const res = await fetch(editId ? `/api/turfs/${editId}` : '/api/turfs', {
        method: editId ? 'PATCH' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) { setNotice(data.error || 'Failed to save turf.'); setBusy(false); return; }
      setShowModal(false);
      await load();
    } catch {
      setNotice('Network error while saving the turf.');
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (t: Turf) => {
    if (!confirm(`Delete "${t.name}" from the directory?`)) return;
    setBusy(true); setNotice('');
    try {
      const res = await fetch(`/api/turfs/${t.id}`, { method: 'DELETE' });
      const data = await res.json();
      if (!res.ok) { setNotice(data.error || 'Could not delete turf.'); return; }
      await load();
    } catch {
      setNotice('Network error while deleting the turf.');
    } finally {
      setBusy(false);
    }
  };

  const doAttach = () => {
    if (!linkTurf || !linkMatchId) return;
    onAttachTurf(linkMatchId, linkTurf);
    setLinkTurf(null); setLinkMatchId('');
    setNotice(`Linked ${linkTurf.name} to the selected match.`);
  };

  const filtered = turfs.filter((t) => {
    if (areaFilter !== 'All' && t.area !== areaFilter) return false;
    if (q.trim()) {
      const needle = q.trim().toLowerCase();
      if (![t.name, t.area, t.address, (t.facilities || []).join(' ')].some((f) => f.toLowerCase().includes(needle))) return false;
    }
    return true;
  });

  const linkCandidates = matches.filter((m) => m.status !== 'cancelled');

  return (
    <div className="max-w-5xl mx-auto space-y-6 pb-20">
      {/* Banner */}
      <div className="bg-slate-900 border border-slate-800 p-6 rounded-3xl flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-black text-white">Turf &amp; Venue Directory</h2>
          <p className="text-xs text-slate-400 mt-1">Discover local turfs, facilities, pricing, and direct contact with each ground.</p>
        </div>
        {isOrganizer && (
          <button
            onClick={openAdd}
            className="px-4 py-2.5 bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-black text-xs rounded-xl transition flex items-center gap-1.5 shadow touch-manipulation"
          >
            <Plus className="w-4 h-4 stroke-[3]" /> Add New Turf
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="space-y-3">
        <div className="relative">
          <Search className="w-4 h-4 text-slate-500 absolute left-3.5 top-1/2 -translate-y-1/2" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name, area, address or facility…"
            className="w-full bg-slate-900 border border-slate-800 rounded-xl text-white text-sm pl-10 pr-4 py-3 focus:outline-none focus:border-emerald-500/60"
          />
        </div>
        <div className="flex gap-2 flex-wrap">
          {['All', ...areas].map((a) => (
            <button
              key={a}
              onClick={() => setAreaFilter(a)}
              className={`px-3.5 py-1.5 rounded-full text-xs font-bold border transition touch-manipulation ${
                areaFilter === a
                  ? 'bg-emerald-500 border-emerald-400 text-slate-950'
                  : 'bg-slate-900 border-slate-800 text-slate-300 hover:border-slate-600'
              }`}
            >
              {a === 'All' ? 'All areas' : a}
            </button>
          ))}
        </div>
      </div>

      {notice && (
        <div className="bg-amber-950/40 border border-amber-700/40 text-amber-200 rounded-xl px-4 py-3 text-xs font-semibold">{notice}</div>
      )}

      {/* Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-16 text-slate-400 text-sm gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading turfs…</div>
      ) : filtered.length === 0 ? (
        <div className="bg-slate-900 border border-slate-800 rounded-3xl py-16 text-center">
          <Landmark className="w-10 h-10 text-slate-700 mx-auto mb-3" />
          <p className="text-slate-400 text-sm font-semibold">No turfs found{areaFilter !== 'All' ? ' in this area' : ''}.</p>
          {isOrganizer && <p className="text-xs text-slate-500 mt-1">Add the first ground to build the directory.</p>}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {filtered.map((t) => (
            <div key={t.id} className="bg-slate-900 border border-slate-800 rounded-3xl overflow-hidden shadow-xl flex flex-col justify-between">
              <div>
                <div className="h-40 w-full relative">
                  <img src={t.imageUrl || 'https://images.unsplash.com/photo-1529900748604-07564a03e7a6?w=800&auto=format&fit=crop&q=80'} alt={t.name} className="w-full h-full object-cover" />
                  {t.rating > 0 && (
                    <div className="absolute top-3 right-3 bg-slate-950/80 backdrop-blur-md px-2.5 py-1 rounded-full text-xs font-bold text-amber-300 flex items-center gap-1 border border-slate-800">
                      <Star className="w-3.5 h-3.5 fill-amber-300" /> {t.rating}
                    </div>
                  )}
                  <div className="absolute bottom-3 left-3 flex gap-1.5">
                    <span className="bg-emerald-950/90 text-emerald-300 text-[10px] font-mono font-bold px-2.5 py-0.5 rounded-full border border-emerald-500/30">{t.size}</span>
                    {t.isIndoor && <span className="bg-slate-950/90 text-slate-300 text-[10px] font-bold px-2.5 py-0.5 rounded-full border border-slate-700">Indoor</span>}
                  </div>
                </div>

                <div className="p-5 space-y-3">
                  <div>
                    <div className="flex items-start justify-between gap-2">
                      <h3 className="font-extrabold text-base text-white leading-snug">{t.name}</h3>
                      {t.area && <span className="shrink-0 text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/25">{t.area}</span>}
                    </div>
                    {t.address && (
                      <p className="text-xs text-slate-400 flex items-center gap-1 mt-0.5"><MapPin className="w-3.5 h-3.5 text-emerald-400 shrink-0" /> {t.address}</p>
                    )}
                  </div>

                  <div className="flex justify-between items-center text-xs font-mono pt-2 border-t border-slate-800/80">
                    <span className="text-slate-400 flex items-center gap-1"><Clock className="w-3.5 h-3.5 text-slate-500" /> Hourly Rate:</span>
                    <span className="font-black text-emerald-400">৳{t.pricePerHour}/hr</span>
                  </div>

                  {t.maxCapacity != null && (
                    <div className="text-xs text-slate-400 flex items-center gap-1.5 font-mono"><Users className="w-3.5 h-3.5 text-blue-400" /> Up to {t.maxCapacity} players</div>
                  )}

                  {t.facilities.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {t.facilities.slice(0, 4).map((f) => (
                        <span key={f} className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-slate-800 text-slate-300 border border-slate-700"><Building2 className="w-2.5 h-2.5 inline mr-1 -mt-0.5" />{f}</span>
                      ))}
                      {t.facilities.length > 4 && <span className="text-[10px] text-slate-500 self-center">+{t.facilities.length - 4} more</span>}
                    </div>
                  )}

                  {t.notes && <p className="text-[11px] text-slate-500 leading-snug">{t.notes}</p>}

                  <div className="flex gap-2 pt-1">
                    {t.phone && (
                      <a href={tellink(t.phone)} className="flex-1 min-h-[40px] py-2 bg-slate-800 hover:bg-slate-700 text-blue-300 font-bold text-xs rounded-xl flex items-center justify-center gap-1.5 border border-slate-700 touch-manipulation">
                        <Phone className="w-3.5 h-3.5" /> Call
                      </a>
                    )}
                    {t.whatsapp ? (
                      <a href={whatslink(t.whatsapp)} target="_blank" rel="noopener noreferrer" className="flex-1 min-h-[40px] py-2 bg-emerald-600/20 hover:bg-emerald-600/30 text-emerald-300 font-bold text-xs rounded-xl flex items-center justify-center gap-1.5 border border-emerald-600/40 touch-manipulation">
                        <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
                      </a>
                    ) : t.phone ? (
                      <a href={whatslink(t.phone)} target="_blank" rel="noopener noreferrer" className="flex-1 min-h-[40px] py-2 bg-emerald-600/20 hover:bg-emerald-600/30 text-emerald-300 font-bold text-xs rounded-xl flex items-center justify-center gap-1.5 border border-emerald-600/40 touch-manipulation">
                        <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
                      </a>
                    ) : null}
                  </div>
                </div>
              </div>

              <div className="p-4 bg-slate-950 border-t border-slate-800/80 space-y-2">
                <a href={t.mapsUrl} target="_blank" rel="noopener noreferrer" className="w-full min-h-[40px] py-2.5 bg-slate-800 hover:bg-slate-700 text-blue-400 hover:text-white font-bold text-xs rounded-xl transition flex items-center justify-center gap-1.5 border border-slate-700 touch-manipulation">
                  <span>Directions on Google Maps</span><Navigation className="w-3.5 h-3.5" />
                </a>
                {canManage(t) && (
                  <div className="grid grid-cols-3 gap-2">
                    <button onClick={() => setLinkTurf(t)} className="min-h-[40px] py-2 bg-emerald-900/40 hover:bg-emerald-800/50 text-emerald-200 font-bold text-xs rounded-xl flex items-center justify-center gap-1 border border-emerald-700/40 touch-manipulation">
                      <Link2 className="w-3.5 h-3.5" /> Link
                    </button>
                    <button onClick={() => openEdit(t)} className="min-h-[40px] py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 font-bold text-xs rounded-xl flex items-center justify-center gap-1 border border-slate-700 touch-manipulation">
                      <Pencil className="w-3.5 h-3.5" /> Edit
                    </button>
                    <button onClick={() => handleDelete(t)} className="min-h-[40px] py-2 bg-red-950/40 hover:bg-red-900/50 text-red-300 font-bold text-xs rounded-xl flex items-center justify-center gap-1 border border-red-800/40 touch-manipulation">
                      <Trash2 className="w-3.5 h-3.5" /> Delete
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md">
          <div className="w-full max-w-lg bg-slate-900 border border-slate-800 rounded-3xl p-5 space-y-4 max-h-[92vh] overflow-y-auto">
            <div className="flex items-center justify-between">
              <h3 className="font-extrabold text-lg text-white">{editId ? 'Edit Turf' : 'Add New Local Turf'}</h3>
              <button onClick={() => setShowModal(false)} className="p-2 text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-3 text-xs">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="sm:col-span-2">
                  <label className="block text-slate-300 font-bold mb-1">Turf Name *</label>
                  <input type="text" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Bashundhara Sports Arena" className="w-full bg-slate-950 border border-slate-700 text-white p-2.5 rounded-xl focus:outline-none focus:border-emerald-500/60" />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-slate-300 font-bold mb-1">Area / Neighbourhood</label>
                  <input type="text" value={form.area} onChange={(e) => setForm({ ...form, area: e.target.value })} placeholder="e.g. Bashundhara R/A" className="w-full bg-slate-950 border border-slate-700 text-white p-2.5 rounded-xl focus:outline-none focus:border-emerald-500/60" />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-slate-300 font-bold mb-1">Address</label>
                  <input type="text" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Full street address" className="w-full bg-slate-950 border border-slate-700 text-white p-2.5 rounded-xl focus:outline-none focus:border-emerald-500/60" />
                </div>
                <div>
                  <label className="block text-slate-300 font-bold mb-1">Hourly Rate (৳) *</label>
                  <input type="number" required min={0} value={form.pricePerHour} onChange={(e) => setForm({ ...form, pricePerHour: parseFloat(e.target.value) || 0 })} className="w-full bg-slate-950 border border-slate-700 text-emerald-400 font-bold font-mono p-2.5 rounded-xl focus:outline-none focus:border-emerald-500/60" />
                </div>
                <div>
                  <label className="block text-slate-300 font-bold mb-1">Size</label>
                  <select value={form.size} onChange={(e) => setForm({ ...form, size: e.target.value })} className="w-full bg-slate-950 border border-slate-700 text-white p-2.5 rounded-xl focus:outline-none focus:border-emerald-500/60">
                    {['5v5', '6v6', '7v7', '8v8', '11v11', 'Custom'].map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-slate-300 font-bold mb-1">Facilities (comma separated)</label>
                  <input type="text" value={form.facilities} onChange={(e) => setForm({ ...form, facilities: e.target.value })} placeholder="e.g. Floodlights, Changing Room, Parking, Water" className="w-full bg-slate-950 border border-slate-700 text-white p-2.5 rounded-xl focus:outline-none focus:border-emerald-500/60" />
                </div>
                <div>
                  <label className="block text-slate-300 font-bold mb-1">Phone *</label>
                  <input type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="01XXXXXXXXX" className="w-full bg-slate-950 border border-slate-700 text-white p-2.5 rounded-xl focus:outline-none focus:border-emerald-500/60" />
                </div>
                <div>
                  <label className="block text-slate-300 font-bold mb-1">WhatsApp</label>
                  <input type="tel" value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} placeholder="01XXXXXXXXX (optional)" className="w-full bg-slate-950 border border-slate-700 text-white p-2.5 rounded-xl focus:outline-none focus:border-emerald-500/60" />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-slate-300 font-bold mb-1">Google Maps URL (optional)</label>
                  <input type="url" value={form.mapsUrl} onChange={(e) => setForm({ ...form, mapsUrl: e.target.value })} placeholder="https://maps.google.com/…" className="w-full bg-slate-950 border border-slate-700 text-white p-2.5 rounded-xl focus:outline-none focus:border-emerald-500/60" />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-slate-300 font-bold mb-1">Image URL (optional)</label>
                  <input type="url" value={form.imageUrl} onChange={(e) => setForm({ ...form, imageUrl: e.target.value })} placeholder="https://…" className="w-full bg-slate-950 border border-slate-700 text-white p-2.5 rounded-xl focus:outline-none focus:border-emerald-500/60" />
                </div>
                <div>
                  <label className="block text-slate-300 font-bold mb-1">Max Players (optional)</label>
                  <input type="number" min={0} value={form.maxCapacity} onChange={(e) => setForm({ ...form, maxCapacity: e.target.value })} className="w-full bg-slate-950 border border-slate-700 text-white p-2.5 rounded-xl focus:outline-none focus:border-emerald-500/60" />
                </div>
                <div className="flex items-end pb-1">
                  <label className="flex items-center gap-2 text-slate-200 font-bold cursor-pointer">
                    <input type="checkbox" checked={form.isIndoor} onChange={(e) => setForm({ ...form, isIndoor: e.target.checked })} className="w-4 h-4 accent-emerald-500" />
                    Indoor turf
                  </label>
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-slate-300 font-bold mb-1">Notes (optional)</label>
                  <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="w-full bg-slate-950 border border-slate-700 text-white p-2.5 rounded-xl focus:outline-none focus:border-emerald-500/60" />
                </div>
              </div>

              <div className="flex gap-2 pt-2">
                <button type="button" onClick={() => setShowModal(false)} className="flex-1 py-2.5 bg-slate-800 text-slate-300 font-bold rounded-xl min-h-[44px]">Cancel</button>
                <button type="submit" disabled={busy} className="flex-1 py-2.5 bg-emerald-500 text-slate-950 font-black rounded-xl min-h-[44px] disabled:opacity-60 flex items-center justify-center gap-2">
                  {busy && <Loader2 className="w-4 h-4 animate-spin" />} {editId ? 'Save Changes' : 'Save Turf'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Link to Match Modal */}
      {linkTurf && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md">
          <div className="w-full max-w-md bg-slate-900 border border-slate-800 rounded-3xl p-5 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-extrabold text-lg text-white">Link Turf to a Match</h3>
              <button onClick={() => setLinkTurf(null)} className="p-2 text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="bg-slate-950 border border-slate-800 rounded-xl p-3">
              <p className="font-bold text-white text-sm">{linkTurf.name}</p>
              {linkTurf.address && <p className="text-xs text-slate-400">{linkTurf.address}</p>}
            </div>
            <label className="block text-xs text-slate-300 font-bold mb-1">Choose a match to attach this turf to</label>
            {linkCandidates.length === 0 ? (
              <p className="text-xs text-slate-500">No active matches available to link.</p>
            ) : (
              <select value={linkMatchId} onChange={(e) => setLinkMatchId(e.target.value)} className="w-full bg-slate-950 border border-slate-700 text-white p-3 rounded-xl text-sm focus:outline-none focus:border-emerald-500/60">
                <option value="">— Select a match —</option>
                {linkCandidates.map((m) => (
                  <option key={m.id} value={m.id}>{(m.status === 'upcoming' ? '🕒 ' : m.status === 'live' ? '🔴 ' : '📋 ') + m.title + (m.date ? ' · ' + m.date : '')}</option>
                ))}
              </select>
            )}
            <div className="flex gap-2 pt-1">
              <button onClick={() => setLinkTurf(null)} className="flex-1 py-2.5 bg-slate-800 text-slate-300 font-bold rounded-xl min-h-[44px] text-xs">Cancel</button>
              <button onClick={doAttach} disabled={!linkMatchId} className="flex-1 py-2.5 bg-emerald-500 text-slate-950 font-black rounded-xl min-h-[44px] text-xs disabled:opacity-50 flex items-center justify-center gap-2">
                <Link2 className="w-4 h-4" /> Attach to Match
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};